import { Box, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ColorPicker } from "react-color-palette";
import MenuPopover from "../extras/MenuPopover";

const trasparentColor = "#ffffff00";

const recommendedColors = [
    {
        hex: '#00AB55',
        rgb: { r: 0, g: 171, b: 85, a: undefined },
        hsv: { h: 150, s: 100, v: 67, a: undefined }
    },
    {
        hex: '#3366FF',
        rgb: { r: 51, g: 102, b: 255, a: undefined },
        hsv: { h: 255, s: 80, v: 100, a: undefined }
    },
    {
        hex: '#FFC107',
        rgb: { r: 255, g: 193, b: 7, a: undefined },
        hsv: { h: 45, s: 97, v: 100, a: undefined }
    },
    {
        hex: '#FF4842',
        rgb: { r: 255, g: 72, b: 66, a: undefined },
        hsv: { h: 2, s: 74, v: 100, a: undefined }
    },
    {
        hex: '#ff006e',
        rgb: { r: 255, g: 0, b: 110, a: undefined },
        hsv: { h: 334, s: 100, v: 100, a: undefined }
    },
    {
        hex: '#540b0e',
        rgb: { r: 84, g: 11, b: 14, a: undefined },
        hsv: { h: 358, s: 87, v: 33, a: undefined }
    },
    {
        hex: '#22577a',
        rgb: { r: 34, g: 87, b: 122, a: undefined },
        hsv: { h: 204, s: 72, v: 48, a: undefined }
    },
    {
        hex: '#7b2cbf',
        rgb: { r: 123, g: 44, b: 191, a: undefined },
        hsv: { h: 272, s: 77, v: 75, a: undefined }
    },
]

const ThemeColorPicker = ({ color = { hex: "#ffffff00" }, setColor, onCommit }) => {

    const [open, setOpen] = useState(null);
    const isDragging = useRef(false);
    const lastColor = useRef(color);

    const handleOpen = (event) => {
        event.stopPropagation();
        setOpen(event.currentTarget);
    };
    
    const handleClose = (event, reason) => {
        // Ignore backdropClick to prevent closing when releasing mouse outside the paper during drag
        if (reason === 'backdropClick') return;
        setOpen(null);
    };

    useEffect(() => {
        const handleMouseUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                if (onCommit) {
                    onCommit(lastColor.current);
                }
            }
        };

        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [onCommit]);

    const handleColorChange = (c) => {
        lastColor.current = c;
        isDragging.current = true;
        setColor(c);
    };

    const theme = useTheme()
    const themeMode = theme.palette.mode;

    return (
        <>
        <IconButton
            onClick={handleOpen}
            sx={{
            py: 0,
            ...(open && {
                '&:before': {
                    zIndex: 1,
                    content: "''",
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    position: 'absolute',
                },
            }),
            }}
            disableRipple={true}
        >
            
            <Box py={0.5} px={1} sx={{ backgroundColor: 'background.paper' }} border={1} borderColor={'divider'} borderRadius={1} >
                <Stack direction={'row'} alignItems={'center'} >
                    
                    <Box
                        height={20}
                        width={20}
                        sx={{ backgroundColor: color.hex }}
                        borderRadius={0.5}
                        border={color.hex === trasparentColor ? 1 : 0}
                        borderColor={'divider'}
                    >
    
                    </Box>
    
                    <Box 
                        height={25}
                        sx={{  }}
                        display={'flex'}
                        alignItems={'center'}
                        px={1.5}
                        borderRadius={0.5}
                    >
                        <Typography fontSize={12} fontWeight={400} color={'text.primary'} >{color.hex === trasparentColor ? 'Transparent' : color.hex}</Typography>
                    </Box>
                </Stack>
            </Box>

        </IconButton>

        <MenuPopover
            open={Boolean(open)}
            anchorEl={open}
            onClose={handleClose}
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            sx={{
                p: 1,
                mt: 1.5,
                ml: 0.75,
                '& .MuiMenuItem-root': {
                    typography: 'body2',
                    borderRadius: 0.75,
                },
                width: 265,
                zIndex: 99999999,
                boxShadow: theme.shadows[24]
            }}
        >
            <Box style={{ zIndex: 99999999 }} onMouseDown={(e) => e.stopPropagation()}>
                <ColorPicker 
                    width={250} 
                    height={150} 
                    color={color} 
                    onChange={handleColorChange} 
                    hideHSV 
                    hideRGB 
                    alpha 
                    dark={themeMode === 'dark'} 
                />
            </Box>

            <Box p={1.5} pt={0} >

                <Typography variant={'caption'} >Recommended colors</Typography>

                <Grid container my={0.5} spacing={2} >
                    {
                        recommendedColors.map((item, index) => {
                            return (
                                <Grid item key={index}>
                                    <Box
                                        height={24}
                                        width={24}
                                        sx={{ backgroundColor: item.hex, outline: `2px solid ${item.hex}`, cursor: 'pointer' }}
                                        borderRadius={26}
                                        border={2}
                                        borderColor={'background.paper'}
                                        onClick={() => {
                                            const c = {
                                                hex: item.hex,
                                                hsv: item.hsv,
                                                rgb: item.rgb
                                            };
                                            setColor(c);
                                            if (onCommit) onCommit(c);
                                        }}
                                    >

                                    </Box>
                                </Grid>
                            )
                        })
                    }
                </Grid>

            </Box>
        </MenuPopover>
        </>
    )
};

export default ThemeColorPicker;